/**
 * @fileoverview Outreach campaign contact filtering and management utilities.
 * Provides functions for contact filtering, attribute handling, variable formatting,
 * and validation for the outreach module's contact management features.
 * @module helperMethods/outreach/index
 */

import { ContactAttribute, OutReachContact } from "@/store/modules/outreach/types";
import { ATTRIBUTE_TYPE } from "./attribute";

/**
 * Filter comparison operators for outreach contact filtering.
 */
export enum OUT_REACH_FILTER_COMPARISON {
  IS = "is",
  CONTAINS = "contains",
}

export interface IOption<T> {
  label: string;
  value: T;
  attributeType?: ATTRIBUTE_TYPE;
  options?: string[];
}

export interface FilterOutReachItem {
  comparison: OUT_REACH_FILTER_COMPARISON;
  variable: keyof OutReachContact;
  value: string | string[];
  valueArray: string[];
}

export const defaultExcludeFields: Array<keyof OutReachContact> = [
  "ID",
  "id",
  "isDuplicate",
  "lastSeen",
  "contactattributes",
];

export const outReachFilterChannel = {
  Whatsapp: "whatsapp",
  Email: ["email", "gmail", "hotmail", "outlook"],
  Phone: ["phone", "phone number"],
  "Web chat": ["webchat", "livechat", "live chat", "chat", "chat widget"],
};

export const haveContainComparisonKey = ["name", "primaryContact"];

/**
 * Converts camelCase string to normal space-separated words with capitalized first letter.
 *
 * @param {string} key - CamelCase string to format
 * @returns {string} Formatted string with spaces and capitalization
 *
 * @example
 * formatCamelCaseToStringNormal('primaryContact');
 * // Returns: "Primary Contact"
 */
export const formatCamelCaseToStringNormal = (key: string): string => {
  const result = key.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

/**
 * Capitalizes the first letter and lowercases the rest of the string.
 *
 * @param {string} key - String to format
 * @returns {string} String with first letter uppercase and rest lowercase
 *
 * @example
 * formatUpperFirstLetter('CONTAINS');
 * // Returns: "Contains"
 */
export const formatUpperFirstLetter = (key: string): string => {
  return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
};

/**
 * Creates a new default filter object with the next available index.
 *
 * @param {FilterOutReachItem} data - Existing filter data to determine next index
 * @returns {Object} Object with new filter entry at next index
 *
 * @example
 * const newFilter = getDefaultFilterOutReach({ 0: {...}, 1: {...} });
 * // Returns: { 2: { variable: undefined, comparison: 'is', value: '', valueArray: [] } }
 */
export const getDefaultFilterOutReach = (
  data: FilterOutReachItem
): { [key: number]: Partial<FilterOutReachItem> } => {
  const maxKey = Math.max(...Object.keys(data).map((key) => +key));
  return {
    [maxKey + 1]: {
      variable: undefined,
      comparison: OUT_REACH_FILTER_COMPARISON.IS,
      value: "",
      valueArray: [],
    },
  };
};

/**
 * Gets available filter variables from contact properties and custom attributes.
 * Excludes specified fields and includes custom contact attributes.
 *
 * @param {Array<keyof OutReachContact>} [excludeFields] - Fields to exclude from options
 * @param {ContactAttribute[]} [attributes] - Custom attributes to include
 * @returns {IOption<string>[]} Array of filter variable options
 *
 * @example
 * const variables = getOutReachVariableExcludeField(['ID'], customAttributes);
 * // Returns: [{ label: 'Name', value: 'name' }, { label: 'Custom Field', value: 'custom_field' }, ...]
 */
export const getOutReachVariableExcludeField = (
  excludeFields: Array<keyof OutReachContact> = defaultExcludeFields,
  attributes: ContactAttribute[] = []
): IOption<string>[] => {
  const outReachContact = new OutReachContact();
  const attributeFilter: IOption<string>[] = attributes.map(
    ({ label, name, value, type: attributeType = ATTRIBUTE_TYPE.TEXT_BOX }) => ({
      label,
      value: name,
      attributeType,
      options: value,
    })
  );
  const defaultFilter = Object.getOwnPropertyNames(outReachContact)
    .sort((a, b) => a.localeCompare(b))
    .reduce((result: IOption<keyof OutReachContact>[], key) => {
      if (!excludeFields.includes(key as keyof OutReachContact)) {
        result.push({
          label: formatCamelCaseToStringNormal(key),
          value: key as keyof OutReachContact,
        });
      }
      return result;
    }, []);
  return [...defaultFilter, ...attributeFilter];
};

/**
 * Gets sortable filter variables with special handling for the 'name' field.
 * Splits 'name' into 'firstName' and 'lastName', excludes non-sortable fields like 'stage' and 'tags'.
 *
 * @param {Array<keyof OutReachContact>} [excludeFields] - Fields to exclude from options
 * @param {ContactAttribute[]} [attributes] - Custom attributes to include
 * @returns {IOption<string>[]} Array of sortable filter variable options
 *
 * @example
 * const sortables = getOutReachSortableVariableExcludeField(['ID'], customAttributes);
 * // Returns: [{ label: 'First Name', value: 'firstName' }, { label: 'Last Name', value: 'lastName' }, ...]
 */
export const getOutReachSortableVariableExcludeField = (
  excludeFields: Array<keyof OutReachContact> = defaultExcludeFields,
  attributes: ContactAttribute[] = []
): IOption<string>[] => {
  const outReachContact = new OutReachContact();
  const attributeFilter: IOption<string>[] = attributes.map(
    ({ label, name, value, type: attributeType = ATTRIBUTE_TYPE.TEXT_BOX }) => ({
      label,
      value: name,
      attributeType,
      options: value,
    })
  );

  const additionalNameFields = [
    { label: "First Name", value: "firstName" as keyof OutReachContact },
    { label: "Last Name", value: "lastName" as keyof OutReachContact },
  ];

  const defaultFilter = Object.getOwnPropertyNames(outReachContact)
    .sort((a, b) => a.localeCompare(b))
    .reduce((result: IOption<keyof OutReachContact>[], key) => {
      if (
        !excludeFields.includes(key as keyof OutReachContact) &&
        !["stage", "tags"].includes(key)
      ) {
        if (key !== "name") {
          result.push({
            label: formatCamelCaseToStringNormal(key),
            value: key as keyof OutReachContact,
          });
        } else {
          additionalNameFields.forEach((additionalNamefield) => result.push(additionalNamefield));
        }
      }
      return result;
    }, []);
  const rs = [...defaultFilter, ...attributeFilter];
  return rs;
};

/**
 * Gets available comparison operators for filtering.
 * Optionally excludes the 'contains' operator based on field type.
 *
 * @param {boolean} [haveContain=true] - Whether to include 'contains' operator
 * @returns {IOption<OUT_REACH_FILTER_COMPARISON>[]} Array of comparison operator options
 *
 * @example
 * const operators = getComparisonOptions(true);
 * // Returns: [{ label: 'Is', value: 'is' }, { label: 'Contains', value: 'contains' }]
 *
 * @example
 * const operatorsExact = getComparisonOptions(false);
 * // Returns: [{ label: 'Is', value: 'is' }]
 */
export const getComparisonOptions = (haveContain = true): IOption<OUT_REACH_FILTER_COMPARISON>[] =>
  Object.keys(OUT_REACH_FILTER_COMPARISON).reduce(
    (result: IOption<OUT_REACH_FILTER_COMPARISON>[], key) => {
      if (haveContain) {
        return [
          ...result,
          {
            label: formatUpperFirstLetter(key),
            value: OUT_REACH_FILTER_COMPARISON[key],
          },
        ];
      } else if (key === OUT_REACH_FILTER_COMPARISON.CONTAINS) {
        return result;
      }
      return result;
    },
    []
  );

/**
 * Gets channel filter options for contact filtering.
 *
 * @returns {Array<{id: string | string[], name: string}>} Array of channel options
 *
 * @example
 * const channels = optionChannelFilter();
 * // Returns: [{ id: 'whatsapp', name: 'Whatsapp' }, { id: ['email', 'gmail'...], name: 'Email' }, ...]
 */
export const optionChannelFilter = () =>
  Object.keys(outReachFilterChannel).map((key) => ({
    id: outReachFilterChannel[key],
    name: key,
  }));

/**
 * Validates if a string is a valid phone number format.
 * Uses regex pattern for North American phone number format (XXX-XXX-XXXX).
 *
 * @param {string} phone - Phone number string to validate
 * @returns {boolean} True if the string matches phone number pattern
 *
 * @example
 * checkIsPhoneNumber('555-123-4567');
 * // Returns: true
 *
 * @example
 * checkIsPhoneNumber('not-a-phone');
 * // Returns: false
 */
export const checkIsPhoneNumber = (phone: string): boolean => {
  const regex = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{3})/;
  // const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,5}$/im;
  return regex.test(phone);
};

// comment for future features

// export const getFilteredListFromList = (data: OutReachContact[], filters: FilterOutReachItem[]) => {
//   return data.reduce((result: OutReachContact[], item) => {
//     let isValid = true;
//     filters.forEach((filter) => {
//       const { comparison, value, variable } = filter;
//       const itemValue = item[variable];
//       const itemFormatted = Array.isArray(itemValue)
//         ? itemValue
//             .map(({ name }) => name)
//             .join(" ")
//             .toLowerCase()
//         : typeof itemValue === "object"
//         ? itemValue.name.toLowerCase() || ""
//         : itemValue.toString().toLowerCase();

//       if (comparison === OUT_REACH_FILTER_COMPARISON.IS) {
//         isValid = itemFormatted === value.toLowerCase();
//       } else {
//         isValid = itemFormatted.includes(value.toLowerCase());
//       }
//     });
//     if (isValid) result.push(item);
//     return result;
//   }, []);
// };

export const OUT_REACH_IMPORT_NOTES = [
  "Tags should be max 20 character including space.",
  "Separate two tags with a comma.",
  `Tags & Stage on CSV file and KeyReply dashboard should match exactly. Else, the field will
  show blank.`,
  `Don't make any changes to the column titles.`,
  `Delete the example contact once
  the purpose is served`,
  `Custom attribute column name must follow template "attribute_name"`,
];

export const OUT_REACH_MODULE = "outreach";
